import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Download, Gem } from 'lucide-react'
import {
  type CrushOrder,
  type CrushPack,
  ApiError,
  createCrushOrder,
  fetchCrushOrder,
  fetchCrushPacks,
  fetchVIPSkus,
  qrisDownloadURL,
} from '../lib/api'
import { downloadFile, openExternalLink, useTelegramBackButton } from '../lib/telegram'
import { hapticNotification } from '../lib/haptics'
import { clearPendingOrder, getPendingOrder, savePendingOrder } from '../lib/pendingOrder'

// How often the Mini App polls order status while awaiting payment — this
// doubles as a reconciliation loop against the payment gateway independent
// of the webhook (see docs/api.md "Payments").
const POLL_INTERVAL_MS = 3000
// Poll rate while the tab/WebView is hidden — see BuyVIPPage.tsx's
// identical constant for why this stays nonzero instead of stopping.
const BACKGROUND_POLL_INTERVAL_MS = 10000

type PageState =
  | { status: 'loading' }
  | { status: 'selecting'; packs: CrushPack[]; selected: number; vipWeeklyPriceIDR: number | null }
  | { status: 'creating' }
  | { status: 'awaiting_payment'; order: CrushOrder }
  | { status: 'completed'; order: CrushOrder }
  // lastPackSize is set from the expired/failed order's own pack_size (when
  // present — see CrushOrder.pack_size's doc comment) so the expired screen
  // can regenerate the same pack in one tap instead of sending the buyer
  // back through the pack picker from scratch.
  | { status: 'expired'; lastPackSize: number | null }
  | { status: 'error'; message: string }

function formatIDR(amount: number): string {
  return 'Rp' + Math.round(amount).toLocaleString('id-ID')
}

function formatCountdown(msRemaining: number): string {
  const totalSeconds = Math.max(0, Math.floor(msRemaining / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

// SAVE X% is computed against the smallest pack's per-unit price as the
// baseline (that pack shows no badge — see docs/ui-references/omi/vip/crush/
// upsell-01.jpeg): larger packs anchor how much cheaper they are per Crush.
function savePercent(pack: CrushPack, baselinePerUnit: number): number {
  const perUnit = pack.price_idr / pack.pack_size
  if (perUnit >= baselinePerUnit) return 0
  return Math.round((1 - perUnit / baselinePerUnit) * 100)
}

export default function BuyCrushPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [state, setState] = useState<PageState>({ status: 'loading' })
  const [now, setNow] = useState(() => Date.now())
  // Tracks whether the hosted QR image (see qris_image_url) failed to load —
  // unlike the old client-rendered SVG, this now depends on a network
  // fetch that can fail, so we need a fallback (see the awaiting_payment
  // render below).
  const [qrImgFailed, setQrImgFailed] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useTelegramBackButton(() => navigate('/profile'))

  function loadPacks() {
    setState({ status: 'loading' })
    Promise.all([fetchCrushPacks(), fetchVIPSkus().catch(() => [])])
      .then(([packs, skus]) => {
        // Specifically the VIP tier's 7-day SKU — skus may now also
        // contain Premium (and eventually Supreme) rows, and this
        // cross-sell ribbon has always meant "cheaper than a week of VIP".
        const weekly = skus.find((s) => s.tier === 'vip' && s.duration_days === 7)
        setState({
          status: 'selecting',
          packs,
          selected: Math.floor(packs.length / 2),
          vipWeeklyPriceIDR: weekly?.price_idr ?? null,
        })
      })
      .catch((e) => {
        setState({
          status: 'error',
          message: e instanceof Error ? e.message : t('common.genericError'),
        })
      })
  }

  // On mount, check for an order left in-flight by a previous visit (see
  // pendingOrder.ts) before falling back to the normal pack picker — same
  // resume logic as BuyVIPPage.tsx.
  useEffect(() => {
    const pendingID = getPendingOrder('crush')
    if (pendingID == null) {
      loadPacks()
      return
    }
    setState({ status: 'loading' })
    fetchCrushOrder(pendingID)
      .then((fresh) => {
        if (fresh.status === 'pending') {
          setState({ status: 'awaiting_payment', order: fresh })
        } else if (fresh.status === 'completed') {
          clearPendingOrder('crush')
          setState({ status: 'completed', order: fresh })
        } else {
          clearPendingOrder('crush')
          setState({ status: 'expired', lastPackSize: fresh.pack_size ?? null })
        }
      })
      .catch(() => {
        clearPendingOrder('crush')
        loadPacks()
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Live countdown tick — separate from the status poll below so the
  // remaining-time display updates every second regardless of the (much
  // slower) poll cadence.
  useEffect(() => {
    if (state.status !== 'awaiting_payment') return
    setQrImgFailed(false)
    tickRef.current = setInterval(() => setNow(Date.now()), 1000)
    return () => {
      if (tickRef.current) clearInterval(tickRef.current)
    }
  }, [state.status])

  // Poll order status while awaiting payment. Stops as soon as a terminal
  // state (completed/expired/failed) is reached.
  useEffect(() => {
    if (state.status !== 'awaiting_payment') return
    const orderId = state.order.id

    function poll() {
      fetchCrushOrder(orderId)
        .then((fresh) => {
          if (fresh.status === 'completed') {
            hapticNotification('success')
            clearPendingOrder('crush')
            setState({ status: 'completed', order: fresh })
          } else if (fresh.status === 'expired' || fresh.status === 'failed') {
            clearPendingOrder('crush')
            setState({ status: 'expired', lastPackSize: fresh.pack_size ?? null })
          }
          // 'pending' — keep polling, nothing to update.
        })
        .catch(() => {
          // Transient poll failure — the next tick tries again.
        })
    }

    // Poll faster while visible, slower (never zero) while hidden — see
    // BACKGROUND_POLL_INTERVAL_MS's doc comment above.
    function scheduleInterval() {
      if (pollRef.current) clearInterval(pollRef.current)
      const ms =
        document.visibilityState === 'visible' ? POLL_INTERVAL_MS : BACKGROUND_POLL_INTERVAL_MS
      pollRef.current = setInterval(poll, ms)
    }

    scheduleInterval()
    document.addEventListener('visibilitychange', scheduleInterval)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
      document.removeEventListener('visibilitychange', scheduleInterval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.status])

  async function handleBuy(packSize: number) {
    setState({ status: 'creating' })
    try {
      const order = await createCrushOrder(packSize)
      savePendingOrder('crush', order.id)
      setState({ status: 'awaiting_payment', order })
    } catch (e) {
      if (e instanceof ApiError && e.status === 502) {
        setState({ status: 'error', message: t('payment.gatewayError') })
      } else {
        setState({
          status: 'error',
          message: e instanceof Error ? e.message : t('common.genericError'),
        })
      }
    }
  }

  return (
    <div className="flex min-h-dvh flex-col bg-tg-bg">
      <div className="flex items-center gap-3 px-4 pt-3">
        <button onClick={() => navigate('/profile')} className="text-sm font-medium text-tg-hint">
          ← {t('common.back')}
        </button>
      </div>

      {state.status === 'loading' && (
        <div className="flex flex-1 items-center justify-center text-tg-hint">
          {t('common.loading')}
        </div>
      )}

      {state.status === 'error' && (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
          <p className="text-sm text-red-500">{state.message}</p>
          <button
            onClick={loadPacks}
            className="rounded-xl bg-tg-secondary-bg px-6 py-3 font-medium text-tg-text"
          >
            {t('common.retry')}
          </button>
        </div>
      )}

      {state.status === 'selecting' &&
        (() => {
          const { packs, selected, vipWeeklyPriceIDR } = state
          const baselinePerUnit = packs.length > 0 ? packs[0].price_idr / packs[0].pack_size : 0

          return (
            <div className="flex flex-col gap-6 px-5 pb-8 pt-2">
              {/* Hero */}
              <div className="flex flex-col items-center gap-3 rounded-2xl bg-gradient-to-b from-cyan-500/20 to-transparent py-8">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-cyan-500">
                  <Gem className="h-8 w-8 fill-current text-white" />
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-tg-text">{t('payment.heroTitle')}</p>
                  <p className="mt-1 text-sm text-tg-hint">{t('payment.heroSubtitle')}</p>
                </div>
              </div>

              {/* Pack cards */}
              <div className="grid grid-cols-3 gap-2">
                {packs.map((pack, i) => {
                  const save = savePercent(pack, baselinePerUnit)
                  const isSelected = i === selected
                  return (
                    <button
                      key={pack.pack_size}
                      onClick={() => setState({ ...state, selected: i })}
                      className={`flex flex-col items-center gap-1.5 rounded-xl border-2 p-3 text-center transition-colors ${
                        isSelected
                          ? 'border-cyan-500 bg-cyan-500 text-white'
                          : 'border-tg-secondary-bg bg-tg-secondary-bg text-tg-text'
                      }`}
                    >
                      <Gem
                        className={`h-5 w-5 fill-current ${isSelected ? 'text-white' : 'text-cyan-500'}`}
                      />
                      <span className="text-sm font-semibold">
                        {t('payment.packLabel', { count: pack.pack_size })}
                      </span>
                      {save > 0 && (
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                            isSelected ? 'bg-white/20 text-white' : 'bg-cyan-500/10 text-cyan-600'
                          }`}
                        >
                          {t('payment.savePercent', { percent: save })}
                        </span>
                      )}
                      <span className={`text-xs ${isSelected ? 'text-white/90' : 'text-tg-hint'}`}>
                        {t('payment.perUnitPrice', {
                          price: formatIDR(pack.price_idr / pack.pack_size),
                        })}
                      </span>
                    </button>
                  )
                })}
              </div>

              <button
                onClick={() => handleBuy(packs[selected].pack_size)}
                disabled={packs.length === 0}
                className="w-full rounded-xl bg-gradient-to-r from-cyan-500 to-emerald-500 py-3.5 text-center font-bold text-white disabled:opacity-50"
              >
                {t('payment.getItNow')}
              </button>

              {/* OR divider + VIP upsell */}
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-tg-secondary-bg" />
                <span className="text-xs font-medium uppercase text-tg-hint">
                  {t('payment.or')}
                </span>
                <div className="h-px flex-1 bg-tg-secondary-bg" />
              </div>

              <button
                onClick={() => navigate('/buy-vip')}
                className="overflow-hidden rounded-xl border border-cyan-500/30 text-left active:opacity-80"
              >
                <div className="bg-cyan-500 px-4 py-2 text-center text-xs font-semibold text-white">
                  {t('payment.vipUpsellRibbon')}
                </div>
                <div className="flex items-center justify-between px-4 py-3">
                  <div>
                    <p className="font-bold text-tg-text">{t('payment.getVip')}</p>
                    {vipWeeklyPriceIDR !== null && (
                      <p className="text-sm text-cyan-600">
                        {t('payment.onlyPerWeek', { price: formatIDR(vipWeeklyPriceIDR) })}
                      </p>
                    )}
                  </div>
                  <span className="text-tg-hint">›</span>
                </div>
              </button>

              <p className="text-center text-xs text-tg-hint">{t('payment.noExpiration')}</p>
            </div>
          )
        })()}

      {state.status === 'creating' && (
        <div className="flex flex-1 items-center justify-center text-tg-hint">
          {t('payment.creatingOrder')}
        </div>
      )}

      {state.status === 'awaiting_payment' && (
        <div className="flex flex-1 flex-col items-center gap-4 px-6 py-6 text-center">
          <p className="text-base font-semibold text-tg-text">{t('payment.awaitingPayment')}</p>
          <p className="text-sm text-tg-hint">{t('payment.scanQris')}</p>
          {/* checkout_url is Saku Rupiah's own hosted invoice page — promoted
              to the primary CTA (not just a fallback for a failed QR image)
              because it handles the e-wallet hand-off itself: tapping it
              opens the OS's default browser, which can deep-link straight
              into the buyer's banking/e-wallet app, where downloading and
              re-scanning a QR image from a second device cannot. */}
          {state.order.checkout_url && (
            <button
              onClick={() => openExternalLink(state.order.checkout_url!)}
              className="w-full max-w-xs rounded-xl bg-tg-button px-4 py-3 text-sm font-semibold text-tg-button-text"
            >
              {t('payment.openPaymentPage')}
            </button>
          )}
          {state.order.qris_image_url && !qrImgFailed ? (
            <>
              {state.order.checkout_url && (
                <p className="text-xs text-tg-hint">{t('payment.or')}</p>
              )}
              <div className="rounded-2xl bg-white p-4">
                <img
                  src={state.order.qris_image_url}
                  alt={t('payment.qrAlt')}
                  width={220}
                  height={220}
                  onError={() => setQrImgFailed(true)}
                />
              </div>
              <button
                onClick={() =>
                  downloadFile(qrisDownloadURL(state.order.order_id), 'swipify-crush-qris.png')
                }
                className="flex items-center gap-2 rounded-xl bg-tg-secondary-bg px-4 py-2 text-sm font-medium text-tg-text"
              >
                <Download className="h-4 w-4" />
                {t('payment.downloadQr')}
              </button>
            </>
          ) : (
            qrImgFailed &&
            !state.order.checkout_url && (
              <p className="text-sm text-tg-hint">{t('payment.qrLoadError')}</p>
            )
          )}
          {state.order.total_payment_idr !== undefined && (
            <p className="text-lg font-bold text-tg-text">
              {formatIDR(state.order.total_payment_idr)}
            </p>
          )}
          {state.order.expires_at && (
            <p className="text-sm text-tg-hint">
              {t('payment.expiresIn', {
                time: formatCountdown(new Date(state.order.expires_at).getTime() - now),
              })}
            </p>
          )}
        </div>
      )}

      {state.status === 'completed' && (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
          <p className="text-xl font-semibold text-tg-text">{t('payment.completedTitle')}</p>
          <p className="text-sm text-tg-hint">
            {t('payment.completedMessage', {
              count: state.order.crush_balance,
            })}
          </p>
          <button
            onClick={() => navigate('/profile')}
            className="rounded-xl bg-primary px-6 py-3 font-medium text-white"
          >
            {t('payment.backToProfile')}
          </button>
        </div>
      )}

      {state.status === 'expired' && (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
          <p className="text-xl font-semibold text-tg-text">{t('payment.expiredTitle')}</p>
          <p className="text-sm text-tg-hint">{t('payment.expiredMessage')}</p>
          {/* One-tap regenerate for the same pack when we know it (see
              PageState's 'expired' variant) — skips sending the buyer back
              through the pack picker from scratch for what's usually just
              "I took longer than the payment window to pay." Falls back to
              the picker when we don't have a pack size to reuse (a stale
              localStorage order predating pack_size). */}
          <button
            onClick={() => (state.lastPackSize ? handleBuy(state.lastPackSize) : loadPacks())}
            className="rounded-xl bg-tg-secondary-bg px-6 py-3 font-medium text-tg-text"
          >
            {t('payment.tryAgain')}
          </button>
        </div>
      )}
    </div>
  )
}
